import { Link } from 'react-router-dom';
import { Checkbox } from '@mantine/core';
import { useTranslation } from '../context/TranslationContext';
import useCheckout from '../hooks/useCheckout';
import FulfillmentSection from '../components/checkout/FulfillmentSection';
import AddressSection from '../components/checkout/AddressSection';
import OrderSummarySection from '../components/checkout/OrderSummarySection';
import PaymentSection from '../components/checkout/PaymentSection';
import './CheckoutConfirm.css';

export default function CheckoutConfirm() {
  const { t } = useTranslation();
  const checkout = useCheckout();

  // Expose handleCheckout to PaymentSection via window (avoids deep prop drilling for non-card methods)
  if (typeof window !== 'undefined') {
    window.__checkoutContinue = checkout.handleCheckout;
  }

  if (!checkout.isAuthenticated) return null;

  return (
    <main className="checkout-confirm-page" aria-labelledby="checkout-confirm-title">
      <h2 id="checkout-confirm-title">{t('orderSummary', 'Order Summary')}</h2>

      <FulfillmentSection
        fulfillmentMethod={checkout.fulfillmentMethod}
        setFulfillmentMethod={checkout.setFulfillmentMethod}
        setSelectedAddressId={checkout.setSelectedAddressId}
        addresses={checkout.addresses}
        locations={checkout.locations}
        holidays={checkout.holidays}
        pickupLocationId={checkout.pickupLocationId}
        setPickupLocationId={checkout.setPickupLocationId}
        pickupTime={checkout.pickupTime}
        setPickupTime={checkout.setPickupTime}
        pickupDate={checkout.pickupDate}
        setPickupDate={checkout.setPickupDate}
      />

      {checkout.fulfillmentMethod === 'delivery' && (
        <AddressSection
          type="shipping"
          addresses={checkout.addresses}
          selectedId={checkout.selectedAddressId}
          onSelectId={checkout.setSelectedAddressId}
          showForm={checkout.showAddressForm}
          setShowAddressForm={checkout.setShowAddressForm}
          form={checkout.addressForm}
          onFormChange={checkout.handleAddressChange}
          onSelectResult={checkout.handleAddressSelect}
          saving={checkout.savingAddress}
          error={checkout.addressError}
          onSubmit={checkout.handleAddAddress}
          deliveryUnavailableIds={checkout.fulfillmentMethod === 'delivery' ? checkout.deliveryUnavailableIds : undefined}
        />
      )}

      <AddressSection
        type="billing"
        addresses={checkout.billingAddresses}
        selectedId={checkout.selectedBillingAddressId}
        onSelectId={checkout.setSelectedBillingAddressId}
        showForm={checkout.showBillingAddressForm}
        setShowAddressForm={checkout.setShowBillingAddressForm}
        form={checkout.billingAddressForm}
        onFormChange={e => {
          const { name, value } = e.target;
          checkout.setBillingAddressForm(f => ({ ...f, [name]: value }));
        }}
        onSelectResult={checkout.handleBillingAddressSelect}
        saving={checkout.savingBillingAddress}
        error={checkout.billingAddressError}
        onSubmit={checkout.handleAddBillingAddress}
        sameAsShipping={checkout.sameAsShippingAddress}
        onSameAsShippingChange={checkout.toggleSameAsShipping}
        selectedShippingAddress={checkout.selectedShippingAddress}
      />

      <OrderSummarySection
        items={checkout.items}
        summary={checkout.summary}
        billingStatus={checkout.billingStatus}
        volumeDiscount={checkout.volumeDiscount}
        appliedBalance={checkout.appliedBalance}
        couponCode={checkout.couponCode}
        setCouponCode={checkout.setCouponCode}
        couponFeedbackKey={checkout.couponFeedbackKey}
        onApplyCoupon={checkout.handleApplyCoupon}
        appliedCouponCodes={checkout.appliedCouponCodes}
        isApplyingCoupon={checkout.isApplyingCoupon}
        isPickup={checkout.fulfillmentMethod === 'pickup'}
      />

      {/* Terms and Pricing Checkboxes */}
      <div
        id="checkout-agreements"
        role="group"
        aria-labelledby="checkout-agreements-label"
        style={{ margin: '20px 0', padding: '16px', background: '#f9f9f9', borderRadius: '8px', border: '1px solid #e2e8f0' }}
      >
        <span id="checkout-agreements-label" className="sr-only">
          {t('checkoutAgreements', 'Required agreements and communication preferences')}
        </span>
        <Checkbox
          checked={checkout.termsAccepted}
          onChange={e => checkout.setTermsAccepted(e.currentTarget.checked)}
          label={
            <span style={{ fontSize: '0.95em', lineHeight: '1.4' }}>
              {t('acceptTerms', 'I accept the')}{' '}
              <a href="/legal" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline' }}>
                {t('termsOfService', 'Terms of Service')}
              </a>
            </span>
          }
          mb="md"
        />
        <Checkbox
          checked={checkout.pricingAcknowledged}
          onChange={e => checkout.setPricingAcknowledged(e.currentTarget.checked)}
          label={
            <span style={{ fontSize: '0.95em', lineHeight: '1.4' }}>
              {t('pricingAcknowledgment', "I understand that the quantity shipped may differ from the quantity ordered, and any resulting balance will be handled via account credit.")}
            </span>
          }
          mb="md"
        />
        <Checkbox
          checked={checkout.marketingOptIn}
          onChange={e => checkout.setMarketingOptIn(e.currentTarget.checked)}
          label={
            <span style={{ fontSize: '0.95em', lineHeight: '1.4' }}>
              {t('marketingOptIn', 'I agree to receive new product announcements and promotions by email.')}
            </span>
          }
        />
      </div>

      <PaymentSection
        termsAccepted={checkout.termsAccepted}
        pricingAcknowledged={checkout.pricingAcknowledged}
        isProcessing={checkout.isProcessing}
        setIsProcessing={checkout.setIsProcessing}
        setError={checkout.setError}
        summary={checkout.summary}
        billingStatus={checkout.billingStatus}
        buildPayload={checkout.buildPayload}
        navigate={checkout.navigate}
        clearCart={checkout.clearCart}
        variantSku={checkout.variantSku}
        deliveryAddressError={checkout.selectedDeliveryMessage}
      />

      {checkout.error && (
        <div
          id="checkout-error"
          role="alert"
          style={{ color: 'red', marginTop: 12 }}
        >
          {checkout.error}
        </div>
      )}

      <nav
        aria-label={t('checkoutNavigation', 'Checkout navigation')}
        style={{ display: 'flex', gap: 12, justifyContent: 'space-between', marginTop: 16 }}
      >
        <Link to="/cart" className="back-link" aria-label={t('backToCart', 'Back to Cart')}>
          ← {t('backToCart', 'Back to Cart')}
        </Link>
        <Link to="/products" className="back-link" aria-label={t('backToProducts', 'Back to Products')}>
          {t('backToProducts', 'Back to Products')} →
        </Link>
      </nav>
    </main>
  );
}